/**
 * ⚠️⚠️ EL MEDIO DE UNA IMPORTACIÓN SÓLO CASABA CON EL id EXACTO.
 *
 * Los tres emparejadores del lado de importación —el del escritor de libro, el
 * del custom-import directo y el de la ruta de plantilla— probaban el texto tal
 * cual y en minúsculas, y nada más. Pero medio catálogo lleva GUION en el id
 * (`cryptocom-exchange`, `kraken-futures`, `binance-futures`) y la propia app
 * produce la forma con guion BAJO en todas partes (`normalizePlatformName`).
 *
 * Medido por la UI el 2026-08-16: con «Nombre del medio» = `cryptocom_exchange`
 * —la forma plegada del id real `cryptocom-exchange`— la importación avisó «no
 * coincide con ningún exchange del catálogo» y escribió las filas con el medio
 * `csv_upload`. El mismo texto SÍ resuelve en la rejilla de transacciones, que
 * pliega separadores desde hace tiempo (y desde #1561 también en los alias).
 *
 * Este módulo es EL emparejador de los CUATRO —los tres del servidor y el aviso
 * del navegador que anuncia el resultado ANTES de importar— y añade un tercer
 * intento: la forma con los separadores plegados. Un medio de texto libre que
 * no case sigue cayendo al `csv_upload` de siempre, con su aviso.
 *
 * Vive en `@bat/shared` justamente porque son cuatro: si el navegador aplicara
 * una regla más estrecha que el servidor, el aviso acusaría a un medio que SÍ
 * va a resolver.
 */

/**
 * Pliega los separadores de un nombre de medio a la forma canónica con `_`.
 * Misma regla que `normalizePlatformName` (@bat/shared) aplica al escribir, y
 * gemela de `foldPlatformSeparators` en el repositorio de transacciones.
 */
export function foldExchangeSeparators(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[.\-\s_]+/g, '_')
    .replace(/_+$/, '')
}

/**
 * Índice `forma plegada → id del catálogo`. Se construye UNA vez por lote (el
 * escritor de libro resuelve fila a fila) y se consulta en memoria.
 *
 * Si dos ids del catálogo pliegan a la misma forma —`crypto_com` y
 * `crypto-com` coexistiendo— gana el PRIMERO en orden alfabético, para que la
 * respuesta no dependa del orden en que la base devuelva las filas.
 */
export function buildFoldedExchangeIndex(
  catalogExchangeIds: Iterable<string>,
): Map<string, string> {
  const index = new Map<string, string>()
  for (const id of [...catalogExchangeIds].sort()) {
    const folded = foldExchangeSeparators(id)
    if (folded !== '' && !index.has(folded)) index.set(folded, id)
  }
  return index
}

/**
 * ⚠️⚠️ Índice `nombre visible plegado → id`, para que valga escribir lo que la
 * app ENSEÑA.
 *
 * Medido por la UI el 2026-08-16 en el importador personalizado: con «Nombre
 * del medio» = «Kraken Exchange» —el nombre que la rejilla, Traza, Balances,
 * el informe fiscal, el panel de admin y «Añadir API» muestran para ese
 * medio— la importación avisó «no coincide con ningún exchange del catálogo»
 * y escribió las filas con `csv_upload`. Su id es `kraken`, y ese texto no
 * aparece en ninguna pantalla. El propio aviso remata: «Escribe el nombre
 * exacto de un exchange del catálogo» — que es justo lo que se había escrito.
 *
 * En el catálogo del entorno, 3 medios reales divergen así: «Kraken Exchange»
 * → `kraken`, «Crypto.com Exchange» → `cryptocom-exchange` y «OKX Futures» →
 * `okex-futures`.
 *
 * 🔑 Un nombre AMBIGUO no se indexa. Hay nombres repetidos de verdad (ocho
 * filas «UP Updated Exchange S6» con ids distintos), y adivinar cuál pondría
 * las filas bajo un medio equivocado EN SILENCIO — peor que el respaldo
 * actual, que al menos se anuncia.
 */
export function buildUnambiguousExchangeNameIndex(
  catalogRows: Iterable<{ id: string; name: string | null }>,
): Map<string, string> {
  const porNombre = new Map<string, Set<string>>()
  for (const row of catalogRows) {
    const folded = foldExchangeSeparators(row.name ?? '')
    if (folded === '') continue
    const ids = porNombre.get(folded) ?? new Set<string>()
    ids.add(row.id)
    porNombre.set(folded, ids)
  }

  const index = new Map<string, string>()
  for (const [folded, ids] of porNombre) {
    // Dos ids distintos con el mismo nombre: no hay respuesta correcta.
    if (ids.size !== 1) continue
    const [id] = [...ids]
    if (id !== undefined) index.set(folded, id)
  }
  return index
}

/**
 * Resolver un medio declarado contra el catálogo: exacto → minúsculas →
 * separadores plegados → NOMBRE visible inequívoco. Devuelve el id o `null`.
 *
 * El nombre va el ÚLTIMO a propósito: si un nombre coincidiera con el id de
 * OTRO medio, manda el id, que es la identidad.
 */
export function matchCatalogExchangeId(
  declared: string,
  // Acepta el `Set` de ids del custom-import y el `Map<id, uuid>` del escritor
  // de libro sin obligar a ninguno de los dos a copiar su colección por fila.
  catalogExchangeIds: ReadonlySet<string> | ReadonlyMap<string, unknown>,
  foldedIndex?: ReadonlyMap<string, string>,
  /**
   * Omitirlo deja el comportamiento EXACTO de antes: los llamadores que no
   * tienen los nombres a mano (el escritor de libro) no cambian.
   */
  nameIndex?: ReadonlyMap<string, string>,
): string | null {
  const trimmed = declared.trim()
  if (trimmed === '') return null
  if (catalogExchangeIds.has(trimmed)) return trimmed

  const lowered = trimmed.toLowerCase()
  if (catalogExchangeIds.has(lowered)) return lowered

  const folded = foldExchangeSeparators(trimmed)
  const index = foldedIndex ?? buildFoldedExchangeIndex(catalogExchangeIds.keys())
  const porId = index.get(folded)
  if (porId !== undefined) return porId

  return nameIndex?.get(folded) ?? null
}
